import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, X } from 'lucide-react';
import { toast } from '../../hooks/useToast';
import { getErrorMessage } from '../../lib/api';
import { operatorCallTargetService } from '../../services/operator-call-target.service';
import { operatorService } from '../../services/operator.service';
import { userService } from '../../services/user.service';
import type {
  CallTargetStatus,
  OperatorCallTarget,
  OperatorCallTargetInput,
} from '../../types/crm';
import type { Operator } from '../../types/crm';
import type { User } from '../../types';
import Loading from '../../components/Loading';

const STATUSES: CallTargetStatus[] = ['planned', 'in_progress', 'completed', 'missed'];

// Map enum strings <-> integer values used by the Rails enum + ransack `_eq`
// filter. The list serializer returns the string form, but ransack on the
// server expects the integer.
const STATUS_INT: Record<CallTargetStatus, number> = {
  planned: 0,
  in_progress: 1,
  completed: 2,
  missed: 3,
};

// react-hook-form's `register` returns strings from <input> and <select>
// elements; we coerce to numbers ourselves in the submit handler so the
// schema input type stays simple strings (matches Operators.tsx pattern).
const schema = z.object({
  operator_id: z.string().min(1, 'Operator is required'),
  user_id: z.string().min(1, 'Rep is required'),
  target_month: z.string().min(1, 'Target month is required'),
  target_visits: z.string().min(1, 'Target visits is required'),
  target_revenue: z.string().optional(),
  notes: z.string().optional(),
});

type FormData = z.infer<typeof schema>;

const titleize = (v: string) => v.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// Take a YYYY-MM input value and return YYYY-MM-01 for the backend.
const monthToFirst = (value: string): string => (value.length === 7 ? `${value}-01` : value);

export const OperatorCallTargets = () => {
  const [targets, setTargets] = useState<OperatorCallTarget[]>([]);
  const [operators, setOperators] = useState<Operator[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Filters
  const [operatorFilter, setOperatorFilter] = useState<string>('');
  const [userFilter, setUserFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<CallTargetStatus | ''>('');
  const [monthFilter, setMonthFilter] = useState<string>('');

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { target_visits: '1' },
  });

  useEffect(() => {
    void loadLookups();
  }, []);

  useEffect(() => {
    void loadTargets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operatorFilter, userFilter, statusFilter, monthFilter]);

  const loadLookups = async () => {
    try {
      const [ops, allUsers] = await Promise.all([
        operatorService.getAll({ per_page: 200 }),
        userService.getAllUsers(),
      ]);
      setOperators(ops.data);
      setUsers(allUsers);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const loadTargets = async () => {
    setIsLoading(true);
    try {
      const params: Record<string, unknown> = {};
      if (operatorFilter) params['q[operator_id_eq]'] = operatorFilter;
      if (userFilter) params['q[user_id_eq]'] = userFilter;
      if (statusFilter) params['q[status_eq]'] = STATUS_INT[statusFilter];
      if (monthFilter) {
        const first = monthToFirst(monthFilter);
        params['q[target_month_gteq]'] = first;
        params['q[target_month_lteq]'] = first;
      }
      const result = await operatorCallTargetService.getAll(params);
      setTargets(result.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  const onCreate = async (data: FormData) => {
    setIsCreating(true);
    try {
      const operatorId = Number(data.operator_id);
      const userId = Number(data.user_id);
      const visits = Number(data.target_visits);
      if (!Number.isFinite(operatorId) || !Number.isFinite(userId) || !Number.isFinite(visits)) {
        toast.error('Please fill in all required fields.');
        return;
      }

      const input: OperatorCallTargetInput = {
        operator_id: operatorId,
        user_id: userId,
        target_month: monthToFirst(data.target_month),
        target_visits: visits,
        target_revenue: data.target_revenue || null,
        notes: data.notes || null,
      };
      await operatorCallTargetService.create(input);
      toast.success('Call target created');
      setShowCreateModal(false);
      reset({ target_visits: '1' });
      void loadTargets();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Operator call targets</h1>
          <p className="text-sm text-gray-600 mt-1">Monthly visit goals per rep / operator.</p>
        </div>
        <button onClick={() => setShowCreateModal(true)} className="btn btn-primary">
          <Plus className="h-4 w-4 inline mr-2" />
          Add target
        </button>
      </div>

      <div className="card p-4 flex flex-wrap gap-3 items-end">
        <div>
          <label className="label">Operator</label>
          <select
            value={operatorFilter}
            onChange={(e) => setOperatorFilter(e.target.value)}
            className="input"
          >
            <option value="">All operators</option>
            {operators.map((op) => (
              <option key={op.id} value={op.id}>{op.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Rep</label>
          <select value={userFilter} onChange={(e) => setUserFilter(e.target.value)} className="input">
            <option value="">All reps</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>{u.full_name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Status</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as CallTargetStatus | '')}
            className="input"
          >
            <option value="">All statuses</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>{titleize(s)}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Month</label>
          <input
            type="month"
            value={monthFilter}
            onChange={(e) => setMonthFilter(e.target.value)}
            className="input"
          />
        </div>
      </div>

      <div className="card overflow-hidden p-0">
        {isLoading ? (
          <div className="p-8"><Loading /></div>
        ) : targets.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No call targets found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Month</th>
                  <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Operator</th>
                  <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rep</th>
                  <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Visits</th>
                  <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Revenue goal</th>
                  <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {targets.map((t) => (
                  <tr key={t.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{t.target_month}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{t.operator_name || '—'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{t.user_full_name || '—'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{t.target_visits}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{t.target_revenue ?? '—'}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        t.status === 'completed' ? 'bg-green-100 text-green-800' :
                        t.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                        t.status === 'missed' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {titleize(t.status)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showCreateModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => setShowCreateModal(false)} />
            <div className="relative inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleSubmit(onCreate)}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Add call target</h3>
                    <button type="button" onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-gray-500">
                      <X className="h-6 w-6" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label htmlFor="operator_id" className="label">Operator</label>
                      <select {...register('operator_id')} id="operator_id" className="input">
                        <option value="">Choose an operator…</option>
                        {operators.map((op) => (
                          <option key={op.id} value={op.id}>{op.name}</option>
                        ))}
                      </select>
                      {errors.operator_id && <p className="mt-1 text-sm text-red-600">{errors.operator_id.message}</p>}
                    </div>

                    <div>
                      <label htmlFor="user_id" className="label">Rep</label>
                      <select {...register('user_id')} id="user_id" className="input">
                        <option value="">Choose a rep…</option>
                        {users.map((u) => (
                          <option key={u.id} value={u.id}>{u.full_name}</option>
                        ))}
                      </select>
                      {errors.user_id && <p className="mt-1 text-sm text-red-600">{errors.user_id.message}</p>}
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="target_month" className="label">Target month</label>
                        <input {...register('target_month')} id="target_month" type="month" className="input" />
                        {errors.target_month && <p className="mt-1 text-sm text-red-600">{errors.target_month.message}</p>}
                      </div>
                      <div>
                        <label htmlFor="target_visits" className="label">Target visits</label>
                        <input {...register('target_visits')} id="target_visits" type="number" min={1} className="input" />
                        {errors.target_visits && <p className="mt-1 text-sm text-red-600">{errors.target_visits.message}</p>}
                      </div>
                    </div>

                    <div>
                      <label htmlFor="target_revenue" className="label">Revenue goal (optional)</label>
                      <input {...register('target_revenue')} id="target_revenue" type="text" placeholder="0.00" className="input" />
                    </div>

                    <div>
                      <label htmlFor="notes" className="label">Notes</label>
                      <textarea {...register('notes')} id="notes" rows={3} className="input" />
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-3">
                  <button type="submit" disabled={isCreating} className="btn btn-primary">
                    {isCreating ? 'Creating…' : 'Create target'}
                  </button>
                  <button type="button" onClick={() => setShowCreateModal(false)} className="btn btn-secondary">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OperatorCallTargets;
